import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { paymentApi } from '../lib/api';
import { CheckCircle, XCircle, Loader2, ArrowRight, Copy, Check, Mail, Key } from 'lucide-react';
import { Button } from '../components/ui/button';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 rounded text-[#78716C] hover:text-[#9A3B26] transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-4 w-4 text-[#2C5E4B]" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const gateway = searchParams.get('gateway');

  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [newCreds, setNewCreds] = useState(null);

  useEffect(() => {
    // Read new-user credentials from sessionStorage (set during verify step)
    try {
      const raw = sessionStorage.getItem('rp_new_creds');
      if (raw) {
        setNewCreds(JSON.parse(raw));
        sessionStorage.removeItem('rp_new_creds');
      }
    } catch {}

    if (gateway === 'razorpay') {
      setStatus('success');
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Poll for Stripe payment status
    const checkStatus = async (attempts = 0) => {
      if (attempts >= 10) { setStatus('timeout'); return; }
      try {
        const response = await paymentApi.getPaymentStatus(sessionId);
        setPaymentDetails(response.data);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => checkStatus(attempts + 1), 2000);
        }
      } catch {
        setTimeout(() => checkStatus(attempts + 1), 2000);
      }
    };
    checkStatus();
  }, [sessionId, gateway]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="max-w-md w-full">

        {/* Checking */}
        {status === 'checking' && (
          <div className="text-center space-y-5">
            <div className="w-20 h-20 mx-auto bg-[#F4EFE6] rounded-full flex items-center justify-center">
              <Loader2 className="h-10 w-10 text-[#9A3B26] animate-spin" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#292524]">Verifying Payment…</h1>
            <p className="text-[#78716C]">Please wait while we confirm your payment</p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="space-y-6">
            {/* Icon + heading */}
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-[#2C5E4B] rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-[#292524]">
                Payment Successful!
              </h1>
              <p className="text-[#78716C]">
                Welcome to the 21-Day Kalaripayattu Programme. You now have full access to all sessions.
              </p>
            </div>

            {/* New user credentials card */}
            {newCreds && (
              <div className="bg-[#F9F5F0] border border-[#E8DFD5] rounded-2xl p-5 space-y-4">
                <h2 className="font-semibold text-[#292524] text-sm">
                  Your login credentials
                </h2>
                <p className="text-xs text-[#78716C]">
                  Your account has been created automatically. Save these credentials now — use them to log in to your dashboard.
                </p>

                {/* Email row */}
                <div className="bg-white rounded-xl border border-[#E8DFD5] px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-[#78716C] mb-1">
                    <Mail className="h-3.5 w-3.5" />
                    <span>Email</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#292524] text-sm break-all">{newCreds.email}</span>
                    <CopyButton text={newCreds.email} />
                  </div>
                </div>

                {/* Password row */}
                <div className="bg-white rounded-xl border border-[#E8DFD5] px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-[#78716C] mb-1">
                    <Key className="h-3.5 w-3.5" />
                    <span>Temporary Password</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-medium text-[#9A3B26] text-sm tracking-wide">
                      {newCreds.password}
                    </span>
                    <CopyButton text={newCreds.password} />
                  </div>
                </div>

                <p className="text-xs text-[#A8A29E]">
                  You can change your password from the dashboard after logging in.
                </p>
              </div>
            )}

            {/* Stripe amount box */}
            {paymentDetails && (
              <div className="bg-[#F4EFE6] rounded-xl p-4">
                <p className="text-sm text-[#78716C]">Amount paid</p>
                <p className="font-heading text-2xl font-bold text-[#9A3B26]">
                  {paymentDetails.currency?.toUpperCase()} {paymentDetails.amount_total}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#9A3B26] hover:bg-[#7A2E1E] text-white rounded-2xl h-14 text-lg font-semibold"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              {newCreds && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/auth?mode=login&email=${encodeURIComponent(newCreds.email)}`)}
                  className="w-full rounded-2xl h-12 border-[#E8DFD5] text-[#292524] hover:bg-[#F4EFE6]"
                >
                  Login to Dashboard
                </Button>
              )}
              <Link to="/" className="block text-center text-[#9A3B26] hover:underline text-sm">
                Back to home
              </Link>
            </div>
          </div>
        )}

        {/* Error / expired / timeout */}
        {['error', 'expired', 'timeout'].includes(status) && (
          <div className="text-center space-y-5">
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#292524]">
              {status === 'expired' ? 'Payment Expired' : 'Payment Failed'}
            </h1>
            <p className="text-[#78716C]">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : status === 'timeout'
                ? 'We could not verify your payment. If you were charged, please contact support.'
                : 'Something went wrong with your payment. Please try again.'}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/#register-section')}
                className="w-full bg-[#9A3B26] hover:bg-[#7A2E1E] text-white rounded-2xl h-14 text-lg font-semibold"
              >
                Try Again
              </Button>
              <a
                href="mailto:support@thekalarimovement.com"
                className="block text-[#9A3B26] hover:underline text-sm"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
